import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/authContext";
import { LazyMotion, domAnimation } from "framer-motion";

// Shared/Core Components
const Layout = lazy(() => import("./Component/Layout"));
import ProtectedRoute from "./Component/ProtectedRoute";
import GlobalLoader from "./Component/GlobalLoader";

// Lazy Load Pages
const Chat = lazy(() => import("./Component/Pages/Chat"));
const Broadcast = lazy(() => import("./Component/Pages/BroadCast"));
const ErrorPage = lazy(() => import("./Component/Pages/ErrorPage"));
const ManageTemplates = lazy(() => import("./Component/Pages/ManageTemplates"));
const TemplateLibrary = lazy(() => import("./Component/Pages/TemplateLibrary"));
const KeywordAction = lazy(() => import("./Component/Pages/KeywordAction"));
const ReplyMaterial = lazy(() => import("./Component/Pages/ReplyMaterial"));
const Chatbot = lazy(() => import("./Component/Pages/Chatbot"));
const ChatbotLibrary = lazy(() => import("./Component/Pages/ChatbotLibrary"));
const Rules = lazy(() => import("./Component/Pages/Rules"));
const Flowbuilder = lazy(() => import("./Component/Flowbuilder/Flowbuilder"));
const Contact = lazy(() => import("./Component/Pages/Contact"));
const Channels = lazy(() => import("./Component/Pages/Channels"));
const ConnectAccount = lazy(() => import("./Component/Channels/ConnectAccount"));
const MessagingLimit = lazy(() => import("./Component/Channels/MessagingLimit"));
const WhatsAppApprovalStatus = lazy(() => import("./Component/Channels/WhatsAppApprovalStatus"));
const WhatsAppDisplayName = lazy(() => import("./Component/Channels/WhatsAppDisplayName"));
const PhoneNumberStatus = lazy(() => import("./Component/Channels/PhoneNumberStatus"));
const QualityRating = lazy(() => import("./Component/Channels/QualityRating"));
const BrocastAnalytics = lazy(() => import("./Component/Pages/BrocastAnalytics"));
const LoginPage = lazy(() => import("./Component/Pages/LoginPage"));
const Register = lazy(() => import("./Component/Pages/Register"));
const ForgotPassword = lazy(() => import("./Component/Pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./Component/Pages/ResetPassword"));
const SetPassword = lazy(() => import("./Component/Pages/SetPassword"));
const VerifyEmail = lazy(() => import("./Component/Pages/VerifyEmail"));
const Dashboard = lazy(() => import("./Component/Pages/Dashboard"));
const UserManagement = lazy(() => import("./Component/UserManagement/UserManagement"));
const WhatsAppCallback = lazy(() => import("./Component/Channels/WhatsAppCallback"));

// Public Pages
// Public Pages - Statically imported to prevent hydration flicker
import LandingPage from "./publicPages/landing/LandingPage";
import PrivacyPolicyPage from "./publicPages/landing/PrivacyPolicyPage";
import FAQPage from "./publicPages/landing/FAQPage";
import ContactPage from "./publicPages/landing/ContactPage";
import AboutPage from "./publicPages/landing/AboutPage";
import PublicLayout from "./publicPages/PublicLayout";

const BlogPage = lazy(() => import("./publicPages/blog/BlogPage"));
const BlogDetail = lazy(() => import("./publicPages/blog/BlogDetail"));
const BlogManagement = lazy(() => import("./publicPages/blogAdmin/BlogManagement"));
const BlogEditor = lazy(() => import("./publicPages/blogAdmin/BlogEditor"));

// const LandingFallback = () => <GlobalLoader fullPage={true} />;

const ConditionalRedirect = () => {
  const { isAuthenticated, loadingUser, user } = useAuth();

  // if (loadingUser) return <LandingFallback />;
  
  if (isAuthenticated) {
    if (user?.role === 'blogEditor') {
      return <Navigate to="/blog-admin" replace />;
    }
    // For any invalid route when logged in, show the internal ErrorPage
    return <ErrorPage />;
  }

  // If not authenticated, redirect to landing page
  return <Navigate to="/" replace />;
};

function App() {
  return (
    <LazyMotion features={domAnimation}>
      <BrowserRouter>
        <Suspense >
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact-us" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
            </Route>
            {/* Public Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/oauth/whatsapp/callback"
              element={<WhatsAppCallback />}
            />

            {/* Blog Admin Routes */}
            <Route
              path="/blog-admin"
              element={
                <ProtectedRoute allowedRoles={["blogEditor"]}>
                  <BlogManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog-admin/create"
              element={
                <ProtectedRoute allowedRoles={["blogEditor"]}>
                  <BlogEditor mode="create" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog-admin/edit/:slug"
              element={
                <ProtectedRoute allowedRoles={["blogEditor"]}>
                  <BlogEditor mode="edit" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/layout"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Chat />} />
              <Route path="broadCast" element={<Broadcast />} />
              <Route path="broadCast-analytics" element={<BrocastAnalytics />} />
              <Route path="manageTemplates" element={<ManageTemplates />} />
              <Route path="templateLibrary" element={<TemplateLibrary />} />
              <Route path="keywordAction" element={<KeywordAction />} />
              <Route path="replyMaterial" element={<ReplyMaterial />} />
              <Route path="chatbot" element={<Chatbot />} />
              <Route path="chatbotLibrary" element={<ChatbotLibrary />} />
              <Route path="rules" element={<Rules />} />
              <Route path="chatbot/flowbuilder" element={<Flowbuilder />} />
              <Route path="contact" element={<Contact />} />
              <Route
                path="channels"
                element={
                  <ProtectedRoute allowedRoles={["admin", "superAdmin"]}>
                    <Channels />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="connect-account" />} />
                <Route path="connect-account" element={<ConnectAccount />} />
                <Route path="messaging-limit" element={<MessagingLimit />} />
                <Route
                  path="whatsapp-approval-status"
                  element={<WhatsAppApprovalStatus />}
                />
                <Route
                  path="whatsapp-display-name"
                  element={<WhatsAppDisplayName />}
                />
                <Route
                  path="phone-number-status"
                  element={<PhoneNumberStatus />}
                />
                <Route path="quality-rating" element={<QualityRating />} />
              </Route>
              <Route path="*" element={<ErrorPage />} />
            </Route>

            {/* Project-specific protected routes */}
            <Route
              path="/project/:projectId"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >

              <Route index element={<Chat />} />
              <Route path="chat" element={<Chat />} />
              <Route path="contact" element={<Contact />} />
              <Route path="broadCast" element={<Broadcast />} />
              <Route path="broadCast-analytics" element={<BrocastAnalytics />} />
              <Route path="manageTemplates" element={<ManageTemplates />} />
              <Route path="templateLibrary" element={<TemplateLibrary />} />
              <Route path="keywordAction" element={<KeywordAction />} />
              <Route path="replyMaterial" element={<ReplyMaterial />} />
              <Route path="chatbot" element={<Chatbot />} />
              <Route path="chatbotLibrary" element={<ChatbotLibrary />} />
              <Route path="rules" element={<Rules />} />
              <Route path="chatbot/flowbuilder" element={<Flowbuilder />} />
              <Route
                path="userManagement"
                element={
                  <ProtectedRoute allowedRoles={["admin", "superAdmin"]}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="channels"
                element={
                  <ProtectedRoute allowedRoles={["admin", "superAdmin"]}>
                    <Channels />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="connect-account" />} />
                <Route path="connect-account" element={<ConnectAccount />} />
                <Route path="messaging-limit" element={<MessagingLimit />} />
                <Route
                  path="whatsapp-approval-status"
                  element={<WhatsAppApprovalStatus />}
                />
                <Route
                  path="whatsapp-display-name"
                  element={<WhatsAppDisplayName />}
                />
                <Route
                  path="phone-number-status"
                  element={<PhoneNumberStatus />}
                />
                <Route path="quality-rating" element={<QualityRating />} />
              </Route>
              <Route path="*" element={<ErrorPage />} />
            </Route>


            {/* Error Route */}
            <Route path="*" element={<ConditionalRedirect />} />

          </Routes>
        </Suspense>
      </BrowserRouter>
    </LazyMotion>
  );
}

export default App;
